import { Position } from 'geojson';
import { useCallback } from 'react';
import { LngLatBoundsLike, useMap } from 'react-map-gl/mapbox';
import { useRecoilValue } from 'recoil';
import { useWindowSize } from 'usehooks-ts';
import { bottomMapPaddingState, leftMapPaddingState, rightMapPaddingState, topMapPaddingState } from '../storage/map';
import { Point } from '../types/geoModels';

const DEFAULT_DURATION = 1400;

export function useCamera() {
  const { map } = useMap();
  const { height } = useWindowSize();
  const leftPadding = useRecoilValue(leftMapPaddingState);
  const rightPadding = useRecoilValue(rightMapPaddingState);
  const bottomPadding = useRecoilValue(bottomMapPaddingState);
  const topPadding = useRecoilValue(topMapPaddingState);

  const zoomIn = () => {
    queueMicrotask(() => map?.zoomIn());
  };

  const zoomOut = () => {
    queueMicrotask(() => map?.zoomOut());
  };

  const zoomTo = useCallback(
    (location: Point | undefined, zoom = 15, duration = DEFAULT_DURATION) => {
      if (location && location.lat && location.lng) {
        queueMicrotask(() => {
          const currentZoom = map?.getZoom() ?? zoom;
          map?.flyTo({
            duration,
            center: location,
            essential: true,
            zoom: currentZoom < zoom ? zoom : currentZoom,
            padding: {
              top: topPadding,
              bottom: bottomPadding,
              left: leftPadding,
              right: rightPadding,
            },
          });
        });
      }
    },
    [leftPadding, map, rightPadding, bottomPadding, topPadding],
  );

  const fitToBounds = useCallback(
    (minLat: number, minLng: number, maxLat: number, maxLng: number, duration = DEFAULT_DURATION) => {
      setTimeout(() => {
        const availableXSpace =
          (map?.getCanvasContainer().offsetWidth ?? 0) - (map?.getPadding().left ?? 0) - (map?.getPadding().right ?? 0);
        const availableYSpace = height - 60 - (map?.getPadding().top ?? 0) - (map?.getPadding().bottom ?? 0);
        const verticalPadding = Math.round(availableYSpace * 0.05);
        const horizontalPadding = Math.round(availableXSpace * 0.05);
        const bounds: LngLatBoundsLike = [
          [minLng, minLat],
          [maxLng, maxLat],
        ];
        map?.fitBounds(bounds, {
          padding: {
            top: topPadding + (verticalPadding ?? 0),
            bottom: bottomPadding + (verticalPadding ?? 0),
            left: leftPadding + (horizontalPadding ?? 0),
            right: rightPadding + (horizontalPadding ?? 0),
          },
          duration: duration,
          essential: true,
        });
      }, 0);
    },
    [leftPadding, map, rightPadding, bottomPadding, topPadding, height],
  );

  const fitToLngLats = useCallback(
    (lngLats?: Position[], duration = DEFAULT_DURATION) => {
      if (!lngLats || lngLats.length === 0) {
        return;
      }
      if (lngLats.length === 1) {
        const Point = {
          lat: lngLats[0][1],
          lng: lngLats[0][0],
        } as Point;
        zoomTo(Point);
        return;
      }
      const lats = lngLats.map((lngLat) => lngLat[1]);
      const lngs = lngLats.map((lngLat) => lngLat[0]);
      const minLng = Math.min(...lngs);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const maxLng = Math.max(...lngs);
      fitToBounds(minLat, minLng, maxLat, maxLng, duration);
    },
    [fitToBounds, zoomTo],
  );

  return { zoomTo, zoomIn, zoomOut, fitToLngLats };
}
